import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, forwardRef } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { CabotageBatch, CabotageEntry, CabotageTransport, ResponseCreateBatchCabotage } from 'src/app/interfaces/cabotageBatches.interfaces';
import { BatchesAvailableTransfer, EntryResponse, ExportOutput, ExportOutputType, ExportTransportOutput, FileBatchExportation, FileEntryExportation, OuputResponse, deleteResponse } from 'src/app/interfaces/exportationBatches.interfaces';
import { EmbargoedMerchandise, ExportTransportCreate, GetLote, MessageError, PreviousRegistration, TranportLine, TransportFile } from 'src/app/interfaces/lotes.interfaces';
import { TonsPerYardService } from '../tons-per-yard/tons-per-yard.service';
import { Router } from '@angular/router';

/**
 * Servicio que gestiona los lotes de cabotaje y sus operaciones asociadas:
 * lotes, entradas, salidas, transportes, cartas membretadas y archivos, además
 * de la carga, filtrado y ordenamiento de la tabla de lotes de cabotaje.
 */
@Injectable({
  providedIn: 'root'
})
export class LoteCabotajeService {
  loader: boolean = false;
  overlayError: boolean = false;
  lotesCabotaje: CabotageBatch[] = [];
  private _lotesCabotaje: CabotageBatch[] = [];
  loteInfoCabotage: CabotageBatch = {
    registry_number:                   0,
    customer_id:                       0,
    id_business_name:                  0,
    address_business:                  '',
    bl_lot_id:                         '',
    customer_lot_number:               '',
    material_group_id:                 0,
    material_id:                       0,
    packing_id:                        0,
    observations:                      '',
    tonnage:                           0,
    quantity_pieces:                   0,
    origin:                            '',
    destination:                       '',
    commercial_value:                   0,
    days_free_storage:                 0,
    storage_fee:                       0,
    special_storage_rate:              0,
    shipper_receiver:                  '',
    address_shipper_receiver:          '',
    damage_violation_record:           '',
    identification_stored_merchandise: '',
    volume_occupied_merchandise:        0,
    merchandise_fiscalized_area:       false,
    consignee_name:                    '',
    address_consignee:                 '',
    container_dimension:               '',
    container_type:                    '',
    number_container_seals:            '',
    number_container_locks:            '',
    unconsolidated_container:          '',
    deconsolidated_store:              '',
    unconsolidated_means_transport:    '',
    consolidated_container:            '',
    consolidated_store:                '',
    consolidated_means_transport:      '',
    pama_number:                       '',
    fiscal_folio_cfdi:                 '',
    total_amount_cfdi:                 '',
    total_amount_presentation:         '',
    amount_merchandise_deposit:        '',
    compensation_amount:               '',
    customs_agent:                     '',
    client_name:                       '',
    material_group_name:               '',
    material_name:                     '',
    packaging:                         '',
    file_cabotage_batch:               '',
    input_tons:                        0,
    output_tons:                       0,
    remaining_tons:                    0,
  }
  cabotageEntries: CabotageEntry[] = [];
  cabotageEntriesFilter: CabotageEntry[] = [];
  cabotageOutputs: ExportOutput[] = [];
  cabotageOutputsFilter: ExportOutput[] = [];
  _batchListError: boolean = false;
  private cabotageBatchesYearSubject = new BehaviorSubject<number>(new Date().getFullYear());
  cabotageBatchesYear$ = this.cabotageBatchesYearSubject.asObservable();

  /**
   * Establece el año de los lotes de cabotaje a consultar.
   * @param year - Año a establecer.
   */
  setCabotageBatchesYear(year: number) {
    this.cabotageBatchesYearSubject.next(year);
  }

  /**
   * Año actualmente seleccionado para los lotes de cabotaje.
   * @returns Año seleccionado.
   */
  get cabotageBatchesYear(): number {
    return this.cabotageBatchesYearSubject.getValue();
  }

  constructor(
    @Inject(forwardRef(() => HttpClient)) private http: HttpClient,
    @Inject(forwardRef(() => TonsPerYardService)) private amountsPerYardSvc: TonsPerYardService,
    private router: Router,
  ) { }

  /**
   * Lista filtrada de lotes de cabotaje.
   * @returns Lista filtrada de lotes de cabotaje.
   */
  get lotesCabotajeFilter() {
    return this._lotesCabotaje;
  }

  set lotesCabotajeFilter(value: CabotageBatch[]) {
    this._lotesCabotaje = value;
  }

  /**
   * Indica si ocurrió un error al cargar el listado de lotes.
   * @returns `true` si hubo error al cargar la lista.
   */
  get batchListError() {
    return this._batchListError;
  }

  set batchListError(_batchListError: boolean) {
    this._batchListError = _batchListError;
  }

  /**
   * Carga y prepara la tabla de lotes de cabotaje de un año, aplicando el
   * mapeo de nombres y el ordenamiento, y gestionando los estados de carga y error.
   * @param year - Año a consultar.
   */
  getLotesCabotajeTabla(year: number) {
    this.lotesCabotaje = [];
    this.lotesCabotajeFilter = [];
    this.loader = true;
    this.listCabotageBatch(year)
    .subscribe({
      next: lotes => {
        if (!Array.isArray(lotes)) {
          this.batchListError = true;
          return;
        }
        lotes = [...this.idToName(lotes)];
        lotes = [...this.sortArray<CabotageBatch>(lotes, 'registry_number')];
        this.lotesCabotaje = [...lotes];
        this.lotesCabotajeFilter = [...lotes];
        this.loader = false;
      },
      error: (e) => {
        this.loader = false;
        this.overlayError = true;
      },
    });
  }

  /**
   * Obtiene un lote de cabotaje por su identificador.
   * @param batchId - Identificador del lote.
   * @returns Observable con el lote de cabotaje.
   */
  getCabotageBatch(batchId: number): Observable<CabotageBatch[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.get}${this.cabotageBatchesYear}/${batchId}`;
    return this.http.get<CabotageBatch[]>(url);
  }

  /**
   * Lista los lotes de cabotaje de un año.
   * @param year - Año a consultar.
   * @returns Observable con la lista de lotes de cabotaje.
   */
  listCabotageBatch(year: number): Observable<CabotageBatch[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.list}/${year}`;
    return this.http.get<CabotageBatch[]>(url);
  }

  /**
   * Obtiene los registros previos asociados a un lote de cabotaje.
   * @param id - Identificador del lote.
   * @returns Observable con los registros previos.
   */
  getCabotagePreviousRegistration( id: number ): Observable<PreviousRegistration[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.previousRegistration}${id}`;
    return this.http.get<PreviousRegistration[]>(url);
  }

  /**
   * Obtiene la mercancía embargada asociada a un lote de cabotaje.
   * @param id - Identificador del lote.
   * @returns Observable con la mercancía embargada.
   */
  getCabotageEmbargoedMerchandise( id: number ): Observable<EmbargoedMerchandise[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.embargoedMerchandise}${id}`;
    return this.http.get<EmbargoedMerchandise[]>(url);
  }

  /**
   * Obtiene el archivo de un lote de cabotaje.
   * @param batchId - Identificador del lote.
   * @returns Observable con el archivo del lote.
   */
  getCabotageBatchFile(batchId: number): Observable<FileBatchExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.file}`;
    return this.http.post<FileBatchExportation>(url, {registry_number: batchId});
  }

  /**
   * Obtiene el archivo de mercancía embargada de un lote de cabotaje.
   * @param registry_number - Número de registro del lote.
   * @param lot_number - Número de lote.
   * @returns Observable con el archivo de mercancía embargada.
   */
  getCabotageEmbargoedFile(registry_number: number, lot_number: number): Observable<FileBatchExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.fileEmbargoedMerchandise}`;
    return this.http.post<FileBatchExportation>(url, {registry_number, lot_number});
  }

  /**
   * Obtiene un archivo anexo de un lote de cabotaje.
   * @param registry_number - Número de registro del lote.
   * @param file_id - Identificador del archivo anexo.
   * @returns Observable con el archivo anexo.
   */
  getAnnexFile(registry_number: number, file_id: number): Observable<FileBatchExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.annexFile}`;
    return this.http.post<FileBatchExportation>(url, {registry_number, file_id});
  }

  /**
   * Crea un lote de cabotaje, seleccionando el endpoint según la ruta actual.
   * @param batch - Datos del lote a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createCabotageBatch(batch: any): Observable<ResponseCreateBatchCabotage> {
    const cabotageType = this.router.url.includes('ship-list') ? GLOBAL.loteCabotaje.createImp : `${GLOBAL.loteCabotaje.create}/2025`;
    const url: string =`${GLOBAL.url}${cabotageType}`;
    return this.http.post<ResponseCreateBatchCabotage>(url, batch);
  }

  /**
   * Actualiza un lote de cabotaje, seleccionando el endpoint según la ruta actual.
   * @param batch - Datos del lote a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateCabotageBatch(batch: any): Observable<{}> {
    const cabotageType = this.router.url.includes('ship-list') ? GLOBAL.loteCabotaje.updateImp : `${GLOBAL.loteCabotaje.create}/2025`;
    const url: string =`${GLOBAL.url}${cabotageType}`;
    return this.http.put<{}>(url, batch);
  }

  /**
   * Elimina un lote de cabotaje de exportación.
   * @param batchId - Número de registro del lote a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteCabotageBatch(batchId: number): Observable<deleteResponse> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.delete}/2025`;
    return this.http.delete<deleteResponse>(url, { headers: new HttpHeaders(), body: {
      registry_number: batchId,
    }});
  }

  /**
   * Elimina un lote de cabotaje de importación.
   * @param batchId - Número de registro del lote a eliminar.
   * @param operation_id - Identificador de la operación.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteCabotageImpBatch(batchId: number, operation_id: number): Observable<deleteResponse> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.deleteImp}`;
    return this.http.delete<deleteResponse>(url, { headers: new HttpHeaders(), body: {
      registry_number: batchId,
      operation_id
    }});
  }

  /**
   * Obtiene el catálogo de líneas de transporte.
   * @returns Observable con la lista de líneas de transporte.
   */
  getListTransportLine(): Observable<TranportLine[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.lotes.listTransportLine}`;
    return this.http.get<TranportLine[]>(url);
  }

  /**
   * Obtiene los transportes de una entrada de cabotaje, normalizando el campo
   * de número de ticket.
   * @param id - Identificador de la entrada.
   * @returns Observable con la lista de transportes.
   */
  getCabotageTransportsByOutput(id: number): Observable<CabotageTransport[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.list}${id}`;
    return this.http.get<CabotageTransport[]>(url).pipe(
      map( transports => {
        return transports.map( transport => {
          transport.no_ticket = transport.no_ticket == 'null' ? '' : transport.no_ticket;
          return transport;
        });
      })
    );
  }

  /**
   * Obtiene el archivo de un transporte de entrada de cabotaje.
   * @param id - Identificador del transporte.
   * @param id_weight - Identificador del pesaje.
   * @returns Observable con el archivo del transporte.
   */
  getCabotageTransportFile(id: number, id_weight: number): Observable<TransportFile> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.file}`;
    return this.http.post<TransportFile>(url, {id, id_weight});
  }

  /**
   * Crea un transporte de entrada de cabotaje.
   * @param transport - Datos del transporte a crear.
   * @returns Observable con el resultado de la operación.
   */
  createCabotageTransport(transport: ExportTransportCreate): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.create}`;
    return this.http.post<MessageError>(url, transport);
  }

  /**
   * Actualiza un transporte de entrada de cabotaje.
   * @param transport - Datos del transporte a actualizar.
   * @returns Observable con el resultado de la operación.
   */
  updateCabotageTransport(transport: ExportTransportCreate): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.update}`;
    return this.http.put<MessageError>(url, transport);
  }

  /**
   * Elimina un transporte de entrada de cabotaje.
   * @param id - Identificador del transporte.
   * @param id_entry - Identificador de la entrada.
   * @returns Observable con el resultado de la operación.
   */
  deleteCabotageTransport(id: number, id_entry: number): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.delete}`;
    return this.http.delete<MessageError>(url, {
      headers: new HttpHeaders(),
      body: {
        id,
        id_entry
      }
    });
  }

  /**
   * Elimina todos los transportes de una entrada de cabotaje.
   * @param id - Identificador de la entrada.
   * @returns Observable con el resultado de la operación.
   */
  deleteAllCabotageTransportEntry(id: number): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.transport.deleteAll}`;
    return this.http.delete<MessageError>(url, {
      headers: new HttpHeaders(),
      body: {
        entry_id: id,
      }
    });
  }

  /**
   * Obtiene los lotes disponibles para transferencia.
   * @param id - Identificador del lote de referencia.
   * @returns Observable con la lista de lotes disponibles para transferencia.
   */
  getBatchesAvailableTransfer(id: number): Observable<BatchesAvailableTransfer[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.batchesAvailableTransfer}${id}`;
    return this.http.get<BatchesAvailableTransfer[]>(url);
  }

  /**
   * Obtiene el listado de entradas de cabotaje.
   * @returns Observable con la lista de entradas de cabotaje.
   */
  getListCabotageEntry(): Observable<CabotageEntry[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.list}`;
    return this.http.get<CabotageEntry[]>(url);
  }

  /**
   * Obtiene las entradas de cabotaje de un lote específico.
   * @param id - Identificador del lote.
   * @returns Observable con la lista de entradas del lote.
   */
  getListByBatchCabotageEntry(id: number): Observable<CabotageEntry[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.listByBatch}${id}`;
    return this.http.get<CabotageEntry[]>(url);
  }

  /**
   * Obtiene una entrada de cabotaje por su identificador.
   * @param id - Identificador de la entrada.
   * @returns Observable con la entrada de cabotaje.
   */
  getCabotageEntry(id: number): Observable<CabotageEntry[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.get}${id}`;
    return this.http.get<CabotageEntry[]>(url);
  }

  /**
   * Obtiene el archivo de una entrada de cabotaje.
   * @param id - Identificador de la entrada.
   * @returns Observable con el archivo de la entrada.
   */
  getFileCabotageEntry(id: number): Observable<FileEntryExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.file}`;
    return this.http.post<FileEntryExportation>(url, {id});
  }

  /**
   * Crea una entrada de cabotaje, agregando la lista de ubicaciones formateada.
   * @param entry - Datos de la entrada a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createCabotageEntry(entry: any): Observable<EntryResponse> {
    entry.location_list = [
      ...this.amountsPerYardSvc.locationsList.map( (location: any) => ({
        ...location,
        location_id: Number(location.location_id),
        tonnage: Number(location.tonnage),
        pieces: Number(location.pieces) || 0
      })),
    ];
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.create}`;
    return this.http.post<EntryResponse>(url, entry);
  }

  /**
   * Actualiza una entrada de cabotaje, agregando la lista de ubicaciones formateada.
   * @param entry - Datos de la entrada a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateCabotageEntry(entry: any): Observable<deleteResponse> {
    entry.location_list = [
      ...this.amountsPerYardSvc.locationsList.map( (location: any) => ({
        ...location,
        location_id: Number(location.location_id),
        tonnage: Number(location.tonnage),
        pieces: Number(location.pieces) || 0
      })),
    ];
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.update}`;
    return this.http.put<deleteResponse>(url, entry);
  }

  /**
   * Elimina una entrada de cabotaje.
   * @param id - Identificador de la entrada a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteCabotageEntry(id: number): Observable<{}> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.delete}`;
    return this.http.delete<{}>(url, { headers: new HttpHeaders(), body: {
      id,
    }});
  }

  /**
   * Crea una carta membretada para un lote de cabotaje.
   * @param cabotage_batch_id - Identificador del lote de cabotaje.
   * @returns Observable con el archivo de la carta membretada creada.
   */
  createMembratedLetter(cabotage_batch_id: number): Observable<FileBatchExportation> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.cartaMembretada.create}`;
    return this.http.post<FileBatchExportation>(url, {cabotage_batch_id});
  }

  /**
   * Obtiene la carta membretada de un lote de cabotaje.
   * @param cabotage_batch_id - Identificador del lote de cabotaje.
   * @returns Observable con el archivo de la carta membretada.
   */
  getMembratedLetter(cabotage_batch_id: number): Observable<FileBatchExportation> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.cartaMembretada.get}`;
    return this.http.post<FileBatchExportation>(url, {cabotage_batch_id});
  }

  /**
   * Crea una salida de cabotaje, agregando la lista de ubicaciones formateada.
   * @param output - Datos de la salida a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createCabotageOutput(output: ExportOutput): Observable<OuputResponse> {
    output.location_list = [
      ...this.amountsPerYardSvc.locationsList.map( (location: any) => ({
        ...location,
        location_id: Number(location.location_id),
        tonnage: Number(location.tonnage),
        pieces: Number(location.pieces) || 0
      })),
    ];
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.create}`;
    return this.http.post<OuputResponse>(url, output);
  }

  /**
   * Actualiza una salida de cabotaje, agregando la lista de ubicaciones formateada.
   * @param output - Datos de la salida a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateCabotageOutput(output: ExportOutput): Observable<deleteResponse> {
    output.location_list = [
      ...this.amountsPerYardSvc.locationsList.map( (location: any) => ({
        ...location,
        location_id: Number(location.location_id),
        tonnage: Number(location.tonnage),
        pieces: Number(location.pieces) || 0
      })),
    ];
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.update}`;
    return this.http.put<deleteResponse>(url, output);
  }

  /**
   * Elimina una salida de cabotaje.
   * @param id - Identificador de la salida a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteCabotageOutput(id: number): Observable<{}> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.delete}`;
    return this.http.delete<{}>(url, { headers: new HttpHeaders(), body: {
      id,
    }});
  }

  /**
   * Obtiene las salidas de un lote de cabotaje.
   * @param id - Identificador del lote.
   * @returns Observable con la lista de salidas del lote.
   */
  getListByBatchCabotageOutput(id: number): Observable<ExportOutput[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.listByBatch}${id}`;
    return this.http.get<ExportOutput[]>(url);
  }

  /**
   * Obtiene una salida de cabotaje por su identificador.
   * @param id - Identificador de la salida.
   * @returns Observable con la salida de cabotaje.
   */
  getCabotageOutput(id: number): Observable<ExportOutput[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.get}${id}`;
    return this.http.get<ExportOutput[]>(url);
  }

  /**
   * Obtiene el archivo de una salida de cabotaje.
   * @param id - Identificador de la salida.
   * @returns Observable con el archivo de la salida.
   */
  getCabotageOutputFile(id: number): Observable<FileEntryExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.file}`;
    return this.http.post<FileEntryExportation>(url, {id});
  }

  /**
   * Obtiene el archivo de retiro de una salida de cabotaje.
   * @param id - Identificador de la salida.
   * @returns Observable con el archivo de retiro.
   */
  getCabotageOutputFileWithdrawal(id: string): Observable<FileEntryExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.fileWithdrawal}`;
    return this.http.post<FileEntryExportation>(url, {id});
  }

  /**
   * Obtiene el archivo de merma de una salida de cabotaje.
   * @param id - Identificador de la salida.
   * @returns Observable con el archivo de merma.
   */
  getCabotageOutputMermaFile(id: number): Observable<FileEntryExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.fileMerma}`;
    return this.http.post<FileEntryExportation>(url, {id});
  }

  /**
   * Obtiene el archivo de acuerdo de una salida de cabotaje.
   * @param id - Identificador de la salida.
   * @returns Observable con el archivo de acuerdo.
   */
  getCabotageOutputAgreementFile(id: number): Observable<FileEntryExportation> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.fileAgreement}`;
    return this.http.post<FileEntryExportation>(url, {id});
  }

  /**
   * Obtiene los tipos de salida disponibles.
   * @returns Observable con la lista de tipos de salida.
   */
  getCabotageOutputTypes(): Observable<ExportOutputType[]> {
    const url: string =`${GLOBAL.url}${GLOBAL.loteExportacion.salidas.tipos}`;
    return this.http.get<ExportOutputType[]>(url);
  }

  /**
   * Asigna un barco de cabotaje y obtiene sus lotes asociados.
   * @param idShip - Identificador del barco.
   * @returns Observable con los lotes del barco.
   */
  assignCabotageShip(idShip: number): Observable<GetLote[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.asignarBarco}${idShip}/cabotaje`;
    return this.http.get<GetLote[]>(url);
  }

  /**
   * Obtiene los lotes de cabotaje asociados a un barco.
   * @param idShip - Identificador del barco.
   * @returns Observable con los lotes del barco.
   */
  getCabotageBatchesByShip(idShip: number): Observable<GetLote[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.listByShip}${idShip}`;
    return this.http.get<GetLote[]>(url);
  }

  /**
   * Obtiene los lotes asociados a un barco de cabotaje.
   * @param idBatch - Identificador del lote o barco.
   * @returns Observable con los lotes asociados.
   */
  batchBoat(idBatch: number): Observable<GetLote[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.loteBarco}${idBatch}/cabotaje`;
    return this.http.get<GetLote[]>(url);
  }

  // Cabotage Transport

  /**
   * Obtiene los transportes de una salida de cabotaje, normalizando el campo
   * de número de ticket.
   * @param id - Identificador de la salida.
   * @returns Observable con la lista de transportes de la salida.
   */
  getCabotageTransportListByOutput(id: number): Observable<ExportTransportOutput[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.transport.list}${id}`;
    return this.http.get<ExportTransportOutput[]>(url).pipe(
      map( transports => {
        return transports.map( transport => {
          transport.no_ticket = transport.no_ticket == 'null' ? '' : transport.no_ticket;
          return transport;
        });
      })
    );
  }

  /**
   * Crea un transporte de salida de cabotaje.
   * @param transport - Datos del transporte a crear.
   * @returns Observable con el resultado de la operación.
   */
  createCabotageTransportOutput(transport: any): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.transport.create}`;
    return this.http.post<MessageError>(url, transport);
  }

  /**
   * Actualiza un transporte de salida de cabotaje.
   * @param transport - Datos del transporte a actualizar.
   * @returns Observable con el resultado de la operación.
   */
  updateCabotageTransportOutput(transport: any): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.transport.update}`;
    return this.http.put<MessageError>(url, transport);
  }

  /**
   * Elimina un transporte de salida de cabotaje.
   * @param id - Identificador del transporte a eliminar.
   * @returns Observable con el resultado de la operación.
   */
  deleteCabotageTransportOutput(id: number): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.transport.delete}`;
    return this.http.delete<MessageError>(url, {
      headers: new HttpHeaders(),
      body: {
        id,
      }
    });
  }

  /**
   * Elimina todos los transportes de una salida de cabotaje.
   * @param id - Identificador de la salida.
   * @returns Observable con el resultado de la operación.
   */
  deleteAllCabotageTransportOutput(id: number): Observable<MessageError> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.salidas.transport.deleteAll}`;
    return this.http.delete<MessageError>(url, {
      headers: new HttpHeaders(),
      body: {
        output_id: id,
      }
    });
  }

  /**
   * Asigna el valor "No asignado." al número de lote del cliente cuando está vacío.
   * @param batch - Lista de lotes de cabotaje a procesar.
   * @returns La lista con los números de lote normalizados.
   */
  idToName(batch: CabotageBatch[]) {
    return batch.map( batch => {
      if (!batch.customer_lot_number) {
        batch.customer_lot_number = 'No asignado.';
      }
      return batch;
    });
  }

  /**
   * Ordena un arreglo de objetos de forma descendente según la clave indicada.
   * @param array - Arreglo a ordenar.
   * @param key - Clave del objeto por la que se ordena.
   * @returns El arreglo ordenado.
   */
  sortArray<T>(array: T[], key: keyof T): T[] {
    return array = array.sort(function (a, b) {
      if (a[key] > b[key]) {
        return -1;
      }
      if (a[key] < b[key]) {
        return 1;
      }
      return 0;
    });
  }
}
